import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  return context?.socket;
};

export const useWebSocketActions = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketActions debe usarse dentro de WebSocketProvider");
  }

  return {
    sendJsonMessage: context.sendJsonMessage,
    addListener: context.addListener,
    isConnected: context.isConnected,
    startWebSocket: context.startWebSocket,
    stopWebSocket: context.stopWebSocket,
  };
};

export const WebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [socketState, setSocketState] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const listeners = useRef([]);
  const heartbeatInterval = useRef(null);
  const reconnectTimeout = useRef(null);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  }, []);

  const sendJsonMessage = useCallback((data) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log("📤 Enviando mensaje:", data);
      socket.send(JSON.stringify(data));
    } else {
      console.warn("🚫 WebSocket no abierto. No se pudo enviar:", data);
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatInterval.current = setInterval(() => {
      sendJsonMessage({ action: "heartbeat" });
    }, 30000);
  }, [stopHeartbeat, sendJsonMessage]);

  const connect = useCallback(() => {
    const token = localStorage.getItem("access");
    if (!token) {
      console.warn("❗ WebSocket no iniciado: no hay token");
      return;
    }

    const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
    const port = window.location.port ? `:${window.location.port}` : "";
    const url = `${wsScheme}://${window.location.hostname}${port}/ws/chat/?token=${token}`;


    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log("🔁 WebSocket ya activo");
      return;
    }

    console.log("📡 Conectando WebSocket:", url);
    const socket = new WebSocket(url);
    socketRef.current = socket;
    setSocketState(socket);

    socket.onopen = () => {
      console.log("✅ WebSocket conectado");
      setIsConnected(true);
      startHeartbeat();

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("📨 [WebSocket] Mensaje recibido:", data?.type || "sin tipo", data);
        listeners.current.forEach((cb) => cb(data));
      } catch (err) {
        console.error("❌ [WebSocket] Error al procesar:", err);
      }
    };

    socket.onclose = () => {
      console.warn("⚠️ WebSocket cerrado.");
      setIsConnected(false);
      setSocketState(null);
      stopHeartbeat();

      reconnectTimeout.current = setTimeout(() => {
        console.log("🔁 Reintentando conexión WebSocket...");
        connect();
      }, 5000);
    };

    socket.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
      socket.close();
    };
  }, [sendJsonMessage, startHeartbeat, stopHeartbeat]);

    const sendLogout = () => {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ action: "logout" }));
      }
    };

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log("👋 Cerrando WebSocket");
      socketRef.current.close();
      socketRef.current = null;
    }
    setSocketState(null);
    setIsConnected(false);
    listeners.current = [];
    stopHeartbeat();

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
  }, [stopHeartbeat]);

  const addListener = (callback) => {
    listeners.current.push(callback);
    return () => {
      listeners.current = listeners.current.filter((cb) => cb !== callback);
    };
  };

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return (
    <WebSocketContext.Provider
      value={{
        socket: socketState,
        isConnected,
        sendJsonMessage,
        addListener,
        startWebSocket: connect,
        stopWebSocket: disconnect,
        sendLogout,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
